import { styled, Switch, SwitchProps } from "@mui/material";
import { DragEvent, KeyboardEvent, useEffect, useState } from "react";
import BootstrapModal from "react-bootstrap/Modal";
import { RxDragHandleDots2 } from "react-icons/rx";
import { ColumnConfig } from "../types";
import useLang from "Shared/hooks/useLanguage";

// Same switch look as the portal column setup (Shared/ColumnSetup/DraggableColumns).
const ColumnSwitch = styled((props: SwitchProps) => (
  <Switch size="small" disableRipple {...props} />
))(({ theme }) => ({
  width: 39,
  height: 22,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transition: theme.transitions.create(["transform"], { duration: 250 }),
    "&.Mui-checked": {
      transform: "translateX(17px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#65C466",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 18, height: 18 },
  "& .MuiSwitch-track": {
    borderRadius: 11,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 300,
    }),
  },
}));

interface Props {
  show: boolean;
  columns: ColumnConfig[];
  onSave: (ordered: ColumnConfig[]) => Promise<void>;
  onClose: () => void;
}

// Show/hide (switch) + drag to reorder. Saves the full ordered list through SaveColumnsV2.
const ColumnsModal = ({ show, columns, onSave, onClose }: Props) => {
  const { t } = useLang();
  const [list, setList] = useState<ColumnConfig[]>([]);
  const [initialKey, setInitialKey] = useState("");
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const keyOf = (l: ColumnConfig[]) =>
    l.map((c) => `${c.id}:${c.isShow ? 1 : 0}`).join(",");

  // Visible columns first (in their current order), then hidden ones.
  useEffect(() => {
    if (!show) return;
    const editable = columns.filter((c) => !c.isExpand && c.isShowOnUi);
    const ordered = [
      ...editable.filter((c) => c.isShow),
      ...editable.filter((c) => !c.isShow),
    ];
    setList(ordered);
    setInitialKey(keyOf(ordered));
    setError(null);
    setDragId(null);
    setDropIndex(null);
  }, [show, columns]);

  const visibleCount = list.filter((c) => c.isShow).length;
  const dirty = keyOf(list) !== initialKey;

  const toggle = (id: number, on: boolean) =>
    setList((l) => l.map((c) => (c.id === id ? { ...c, isShow: on } : c)));

  const moveTo = (id: number, to: number) =>
    setList((l) => {
      const from = l.findIndex((c) => c.id === id);
      if (from < 0) return l;
      const n = [...l];
      const [col] = n.splice(from, 1);
      n.splice(from < to ? to - 1 : to, 0, col);
      return n;
    });

  // ------------------------------------------------------------ drag & drop
  const onDragOver = (e: DragEvent<HTMLLIElement>, index: number) => {
    if (dragId === null) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropIndex(e.clientY < rect.top + rect.height / 2 ? index : index + 1);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    if (dragId !== null && dropIndex !== null) moveTo(dragId, dropIndex);
    endDrag();
  };
  const endDrag = () => {
    setDragId(null);
    setDropIndex(null);
  };

  // Keyboard: Alt + ↑/↓ moves the focused row.
  const onKeyDown = (e: KeyboardEvent<HTMLLIElement>, index: number) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const to = e.key === "ArrowUp" ? index - 1 : index + 2;
    if (to < 0 || to > list.length) return;
    const id = list[index].id;
    moveTo(id, to);
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>(`[data-col-id="${id}"]`)?.focus()
    );
  };

  const save = async () => {
    if (!visibleCount) return setError(t("Pick at least one column."));
    setSaving(true);
    setError(null);
    try {
      await onSave(list);
      onClose();
    } catch {
      setError(t("Could not save the column layout."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BootstrapModal
      show={show}
      onHide={onClose}
      backdrop="static"
      centered
      dialogClassName="vr2-cm-dialog"
      contentClassName="vr2-cm-content"
    >
      <BootstrapModal.Header closeButton className="py-4 px-6">
        <div>
          <BootstrapModal.Title className="h5 mb-1">
            {t("Show Hide Columns")}
          </BootstrapModal.Title>
          <div className="text-gray-600 fs-7">
            {t("Turn columns on or off and drag to change their order.")}
          </div>
        </div>
      </BootstrapModal.Header>

      <BootstrapModal.Body className="p-0">
        <ol
          className="vr2-cm-list"
          onDragOver={(e) => dragId !== null && e.preventDefault()}
          onDrop={onDrop}
        >
          {list.map((c, i) => (
            <li
              key={c.id}
              data-col-id={c.id}
              tabIndex={0}
              draggable
              className={[
                "vr2-cm-item",
                !c.isShow && "is-off",
                dragId === c.id && "is-dragging",
                dropIndex === i && "drop-before",
                dropIndex === list.length && i === list.length - 1 && "drop-after",
              ]
                .filter(Boolean)
                .join(" ")}
              onDragStart={(e) => {
                setDragId(c.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(c.id));
              }}
              onDragOver={(e) => onDragOver(e, i)}
              onDragEnd={endDrag}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              <RxDragHandleDots2 className="vr2-cm-grip" aria-hidden="true" />
              <span className="vr2-cm-label">{t(c.label)}</span>
              <ColumnSwitch
                checked={c.isShow}
                onChange={(e) => toggle(c.id, e.target.checked)}
                inputProps={{ "aria-label": `${t("Show")} ${t(c.label)}` }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </li>
          ))}
        </ol>
        {error && <div className="text-danger fs-7 px-6 pb-3">{error}</div>}
      </BootstrapModal.Body>

      <BootstrapModal.Footer className="py-3 px-6 justify-content-between">
        <span className="text-gray-600 fs-7">
          <b className="text-gray-900">{visibleCount}</b> {t("of")}{" "}
          {list.length} {t("columns visible")}
        </span>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving || !dirty || !visibleCount}
            onClick={save}
          >
            {saving ? t("Saving...") : t("Save")}
          </button>
        </div>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default ColumnsModal;
